import type { FitmentProduct, FitmentVariant } from '../../api/product-fitments-api';
import { Button } from '@eliadmin/core/src/components/ui/button';

const HIDDEN_VARIANT_ATTRIBUTES = new Set(['brand', 'chain', 'chain_complete', 'značka']);

function normalizeAttributeKey(value?: string | null) {
  return value?.trim().toLowerCase().replaceAll(' ', '_') ?? '';
}

function getVariantCountLabel(count: number) {
  if (count === 1) return 'verzia';
  if (count % 100 >= 11 && count % 100 <= 14) return 'verzií';
  if (count % 10 >= 2 && count % 10 <= 4) return 'verzie';
  return 'verzií';
}

export function getFitmentVariantLabel(variant: FitmentVariant) {
  const seenValues = new Set<string>();
  const valuesWithAttributes = (variant.values ?? [])
    .map(value => {
      const label = (value.value || value.name)?.trim();
      const attributeKey = normalizeAttributeKey(
        value.attribute?.external_id || value.attribute?.name || value.attribute_name,
      );

      return {
        label,
        attributeKey,
      };
    })
    .filter(value => value.label && !HIDDEN_VARIANT_ATTRIBUTES.has(value.attributeKey))
    .filter(value => {
      const normalizedLabel = value.label!.toLocaleLowerCase();
      if (seenValues.has(normalizedLabel)) return false;
      seenValues.add(normalizedLabel);
      return true;
    });

  const code = variant.ext_code || variant.code;
  const preferredValues = [
    valuesWithAttributes.find(value => value.attributeKey === 'model_year'),
    valuesWithAttributes.find(value => ['spec', 'version'].includes(value.attributeKey)) ??
      valuesWithAttributes.find(value => value.attributeKey === 'variant'),
    valuesWithAttributes.find(value => ['colour', 'color'].includes(value.attributeKey)),
  ]
    .filter(value => value !== undefined)
    .map(value => value.label!);
  const meaningfulValues = (
    preferredValues.length > 0 ? preferredValues : valuesWithAttributes.map(value => value.label!)
  ).slice(0, 3);

  if (meaningfulValues.length > 0) {
    return [...meaningfulValues, code].filter(Boolean).join(' · ');
  }

  return variant.name || code || `Variant #${variant.id}`;
}

export function FitmentVariantSummary({
  product,
  mode = 'compact',
}: {
  product: FitmentProduct;
  mode?: 'compact' | 'details';
}) {
  if (!product.variant_fitments_differ) return null;

  const variants = product.fitment_variants ?? [];

  if (mode === 'details' && variants.length > 0) {
    return (
      <details className="group max-w-xl text-xs">
        <summary className="cursor-pointer text-muted-foreground marker:text-primary hover:text-foreground">
          Kompatibilné verzie · {variants.length} {getVariantCountLabel(variants.length)}
        </summary>
        <ul className="mt-2 max-h-48 space-y-1 overflow-y-auto border-l pl-3 text-foreground">
          {variants.map(variant => (
            <li key={variant.id}>{getFitmentVariantLabel(variant)}</li>
          ))}
        </ul>
      </details>
    );
  }

  const compactLabel =
    variants.length > 0
      ? `${variants.length} ${getVariantCountLabel(variants.length)}`
      : 'Podľa verzie';

  return (
    <span
      aria-label={`Kompatibilita podľa verzie: ${compactLabel}`}
      className="inline-flex max-w-full items-center gap-2 rounded-full bg-muted px-2.5 py-1 text-xs text-muted-foreground"
    >
      <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-primary" aria-hidden="true" />
      <span className="font-medium text-foreground">{compactLabel}</span>
    </span>
  );
}

export function FitmentPagination({
  page,
  lastPage,
  onPageChange,
}: {
  page: number;
  lastPage: number;
  onPageChange: (page: number) => void;
}) {
  if (lastPage <= 1) return null;

  return (
    <div className="grid grid-cols-2 items-center justify-center gap-2 sm:flex sm:gap-3">
      <Button
        type="button"
        size="sm"
        variant="outline"
        className="w-full sm:order-1 sm:w-auto"
        disabled={page <= 1}
        onClick={() => onPageChange(page - 1)}
      >
        Predchádzajúca
      </Button>
      <Button
        type="button"
        size="sm"
        variant="outline"
        className="w-full sm:order-3 sm:w-auto"
        disabled={page >= lastPage}
        onClick={() => onPageChange(page + 1)}
      >
        Ďalšia
      </Button>
      <span className="col-span-2 text-center text-sm text-muted-foreground sm:order-2 sm:col-auto">
        Strana {page} z {lastPage}
      </span>
    </div>
  );
}
